import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MessageCircle, ShoppingBag, Heart, MapPin, Star, ArrowLeft, Sparkles, Zap, Eye, MousePointerClick, Pencil, Trash2, CheckCircle2, FileText } from "lucide-react";
import AppShell from "@/components/AppShell";
import VerifiedBadge from "@/components/VerifiedBadge";
import TrustScoreRing from "@/components/TrustScoreRing";
import ShareButton from "@/components/ShareButton";
import RichDescription from "@/components/RichDescription";
import PdfViewer from "@/components/PdfViewer";
import FullscreenImage from "@/components/FullscreenImage";
import { useAuth, ROLE_THEMES } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatPriceText } from "@/lib/format";
import { LISTING } from "@/constants/testIds";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1560493676-04071c5f467b?auto=format&fit=crop&w=1200&q=80";

export default function ListingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const theme = user ? ROLE_THEMES[user.role] : ROLE_THEMES.farmer;
  const [item, setItem] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showBoost, setShowBoost] = useState(false);
  const [pm, setPm] = useState("COD");
  const [addr, setAddr] = useState("");
  const [phone, setPhone] = useState(user?.mobile || "");
  const [note, setNote] = useState("");
  const [customItems, setCustomItems] = useState("");
  const [qty, setQty] = useState(1);
  const [buyerCoords, setBuyerCoords] = useState({ lat: null, lng: null });
  const [deliveryChoice, setDeliveryChoice] = useState("delivery"); // 'delivery' | 'pickup'
  const [estimate, setEstimate] = useState(null);
  const [locating, setLocating] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const isOwner = user?.id && item?.owner_id && user.id === item.owner_id;

  // Recompute estimate whenever inputs change
  useEffect(() => {
    if (!showCheckout || !item) return;
    api.get("/orders/estimate", {
      params: {
        listing_id: id, buyer_lat: buyerCoords.lat, buyer_lng: buyerCoords.lng,
        payment_method: pm, quantity: qty, delivery_choice: deliveryChoice,
      },
    }).then((r) => setEstimate(r.data)).catch(() => setEstimate(null));
  }, [showCheckout, item, pm, qty, buyerCoords.lat, buyerCoords.lng, deliveryChoice, id]);

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not available"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setBuyerCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setLocating(false); toast.success("Delivery location set"); },
      (err) => { setLocating(false); toast.error("Location: " + err.message); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Multi-product cart from the same seller
  const [sellerListings, setSellerListings] = useState([]);
  const [cart, setCart] = useState({}); // { listing_id: qty }
  useEffect(() => {
    if (!showCheckout || !item || item.kind !== "product") return;
    api.get("/listings", { params: { kind: "product" } })
      .then((r) => {
        const others = r.data.filter((l) => l.owner_id === item.owner_id && l.id !== item.id);
        setSellerListings(others);
      })
      .catch(() => setSellerListings([]));
  }, [showCheckout, item]);

  const cartCount = Object.values(cart).filter((v) => v > 0).length;
  const cartTotalItems = 1 + cartCount; // primary + extras
  const [analytics, setAnalytics] = useState(null);
  const [detailOpen, setDetailOpen] = useState(null); // { kind, items }
  const [loadingDetail, setLoadingDetail] = useState(false);

  const openAnalytics = async (kind) => {
    setLoadingDetail(true);
    setDetailOpen({ kind, items: [] });
    try {
      const { data } = await api.get(`/listings/${id}/analytics/detail`, { params: { kind } });
      setDetailOpen({ kind, items: data.items });
    } catch (e) { toast.error("Could not load details"); setDetailOpen(null); }
    finally { setLoadingDetail(false); }
  };

  useEffect(() => {
    api.get(`/listings/${id}`).then((r) => {
      setItem(r.data);
      // Track view once loaded
      if (r.data.owner_id !== user?.id) {
        api.post(`/listings/${id}/track?event=view`).catch(() => {});
      } else {
        api.get(`/listings/${id}/analytics`).then((a) => setAnalytics(a.data)).catch(() => {});
      }
    }).catch(() => nav("/marketplace"));
  }, [id, nav, user]);

  const trackClick = () => api.post(`/listings/${id}/track?event=click`).catch(() => {});

  const boost = async () => {
    try {
      const { data } = await api.post("/boost/checkout", { listing_id: id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const interested = async () => {
    trackClick();
    try {
      await api.post("/interests", { listing_id: id, message: "I am interested" });
      toast.success("Interest sent to " + item.owner_name);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const chat = async () => {
    trackClick();
    try {
      await api.post("/messages", { to_user_id: item.owner_id, listing_id: id, body: `Hi, I'm interested in ${item.name}` });
      toast.success("Message sent");
      nav("/messages");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const placeOrder = async () => {
    if (deliveryChoice === "delivery") {
      if (!addr.trim()) return toast.error("Please enter delivery address");
      if (!phone.trim() || phone.replace(/\D/g, "").length < 10) return toast.error("Please enter a valid 10-digit phone");
    }
    try {
      const extras = Object.entries(cart).filter(([, q]) => q > 0).map(([lid, q]) => ({ listing_id: lid, quantity: q }));
      let order;
      if (extras.length > 0) {
        const { data } = await api.post("/orders/multi", {
          seller_id: item.owner_id,
          items: [{ listing_id: id, quantity: qty }, ...extras],
          payment_method: pm,
          delivery_address: addr, buyer_phone: phone, buyer_note: note,
          buyer_lat: buyerCoords.lat, buyer_lng: buyerCoords.lng,
          delivery_choice: deliveryChoice,
          custom_items: customItems,
        });
        order = data;
      } else {
        const { data } = await api.post("/orders", {
          listing_id: id, quantity: qty, payment_method: pm,
          delivery_address: addr, buyer_phone: phone, buyer_note: note,
          buyer_lat: buyerCoords.lat, buyer_lng: buyerCoords.lng,
          delivery_choice: deliveryChoice,
          custom_items: customItems,
        });
        order = data;
      }
      if (pm === "COD") {
        toast.success(`Order placed! Pay on delivery.`);
        setShowCheckout(false);
        nav("/orders");
        return;
      }
      // Online payment via Stripe
      const { data: co } = await api.post("/payments/checkout", { order_id: order.id, origin_url: window.location.origin });
      window.location.href = co.checkout_url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const editPost = () => nav(`/edit/${id}`);

  const deletePost = async () => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    try {
      await api.delete(`/listings/${id}`);
      toast.success("Post deleted");
      nav("/marketplace");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const markComplete = async () => {
    if (!window.confirm("Mark this post as completed? It will be hidden from the marketplace.")) return;
    try {
      await api.post(`/listings/${id}/complete`);
      toast.success("Marked as completed");
      setItem((prev) => ({ ...prev, status: "completed" }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!item) return <AppShell><div className="p-12 text-center text-gray-500">Loading...</div></AppShell>;

  const imgs = item.images?.length ? item.images : [FALLBACK_IMG];

  return (
    <AppShell>
      <div className="px-4 md:px-8 lg:px-12 py-6">
        <button onClick={() => nav(-1)} className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center gap-2"><ArrowLeft className="h-4 w-4" />Back</button>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <div className="rounded-3xl overflow-hidden bg-white border border-gray-100 premium-shadow">
              <div className="aspect-[4/3] bg-gray-50 flex items-center justify-center">
                <img src={imgs[imgIdx]} alt="" onClick={() => setLightbox(true)} onError={(e) => { e.currentTarget.style.opacity = "0.5"; }} className="w-full h-full object-contain cursor-zoom-in" data-testid="listing-main-image" />
              </div>
            </div>
            {imgs.length > 1 && (
              <div className="mt-3 flex gap-2 overflow-x-auto">
                {imgs.map((im, i) => (
                  <button key={`thumb-${i}-${im.slice(0, 12)}`} onClick={() => setImgIdx(i)} className={`h-16 w-16 rounded-xl overflow-hidden border-2 ${i === imgIdx ? theme.border : "border-transparent"}`}>
                    <img src={im} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <div className="flex items-center gap-2 flex-wrap">
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] uppercase tracking-widest ${theme.bgSoft} ${theme.text}`}>
                {item.kind} · {item.category}
                {item.is_featured && <span className="inline-flex items-center gap-1 ml-1 px-2 py-0.5 rounded-full bg-amber-500 text-white"><Sparkles className="h-2.5 w-2.5" />{t("listing.featured")}</span>}
              </div>
              {item.status === "completed" && (
                <span data-testid="listing-completed-badge" className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-[10px] uppercase tracking-widest">
                  <CheckCircle2 className="h-3 w-3" />Completed
                </span>
              )}
              {item.expires_at && new Date(item.expires_at) > new Date() && (
                <span data-testid="listing-expiry-badge" className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-orange-50 text-orange-700 border border-orange-200 text-[10px] uppercase tracking-widest">
                  Expires in {daysUntil(item.expires_at)}
                </span>
              )}
              <div className="ml-auto">
                <ShareButton id={item.id} title={item.name} price={item.price}
                  location={[item.seller_village, item.seller_district, item.seller_state].filter(Boolean).join(", ")} />
              </div>
            </div>
            <h1 className="mt-3 text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">{item.name}</h1>
            <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{item.location || "—"}</span>
              <span className="flex items-center gap-1 text-amber-500"><Star className="h-3.5 w-3.5 fill-amber-500" />{item.avg_rating ? `${item.avg_rating} · ${item.review_count} reviews` : "No reviews yet"}</span>
            </div>
            <div className={`mt-6 text-4xl font-bold ${theme.text}`} data-testid="listing-price">{formatPriceText(item)}</div>

            <div className="mt-6">
              <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold mb-2">About this {item.kind || "post"}</div>
              <RichDescription text={item.description} />
            </div>

            {item.project_pdf && (
              <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-4 flex items-center gap-3">
                <div className={`h-10 w-10 rounded-xl ${theme.bgSoft} ${theme.text} flex items-center justify-center`}>
                  <FileText className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-gray-900 text-sm">Project document</div>
                  <div className="text-xs text-gray-500">{isOwner ? "You can view, replace or remove this." : "View-only • download disabled"}</div>
                </div>
                <button data-testid="listing-view-pdf" onClick={() => setShowPdf(true)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold ${theme.bg} text-white`}>View</button>
              </div>
            )}

            <div className="mt-6 rounded-2xl bg-white border border-gray-100 p-5">
              <div className="flex items-center gap-4">
                <div className={`h-12 w-12 rounded-full ${theme.bg} text-white flex items-center justify-center font-bold`}>{item.owner_name?.[0]}</div>
                <div>
                  <div className="font-semibold text-gray-900 flex items-center gap-2">{item.owner_name}{item.owner_verified && <VerifiedBadge small />}</div>
                  <div className="text-xs text-gray-500 uppercase tracking-widest">{item.owner_verified ? "Verified " : ""}{theme.name}</div>
                </div>
                {item.owner_trust && <TrustScoreRing score={item.owner_trust.score} size={56} thin className="ml-auto" />}
              </div>
              {item.contact_locked ? (
                <div className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800">
                  🔒 Click <b>Interested</b> or <b>Buy Now</b> to contact this seller via Chat.
                </div>
              ) : (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  {item.lat && item.lng && <a href={`https://www.google.com/maps?q=${item.lat},${item.lng}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-50 text-blue-800 border border-blue-200"><MapPin className="h-3 w-3" />View on Map</a>}
                  {(item.seller_village || item.seller_district || item.seller_state) && <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-50 text-gray-700 border border-gray-200"><MapPin className="h-3 w-3" />{[item.seller_village, item.seller_district, item.seller_state].filter(Boolean).join(", ")}</div>}
                </div>
              )}
              <div className="mt-4 text-xs text-gray-600 flex items-center gap-2 border-t border-gray-100 pt-3">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-50 border border-gray-200">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  <span className="font-medium text-gray-700">{relativeTime(item.created_at)}</span>
                </span>
                <span className="text-gray-400" data-testid="listing-posted-at">
                  Posted on {new Date(item.created_at).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            </div>

            {item.owner_id === user.id && analytics && (
              <div className="mt-4 rounded-2xl bg-white border border-gray-100 p-5">
                <div className="text-xs uppercase tracking-widest text-gray-500">{t("analytics.title")}</div>
                <div className="mt-3 grid grid-cols-4 gap-3">
                  <AnalyticsCell icon={Eye} label={t("analytics.views")} value={analytics.views} color="text-blue-600" bg="bg-blue-50" onClick={() => openAnalytics("views")} tid="analytics-views" />
                  <AnalyticsCell icon={MousePointerClick} label={t("analytics.clicks")} value={analytics.clicks} color="text-emerald-600" bg="bg-emerald-50" onClick={() => openAnalytics("clicks")} tid="analytics-clicks" />
                  <AnalyticsCell icon={Heart} label={t("analytics.interests")} value={analytics.interests} color="text-pink-600" bg="bg-pink-50" onClick={() => openAnalytics("interests")} tid="analytics-interests" />
                  <AnalyticsCell icon={ShoppingBag} label={t("analytics.orders")} value={analytics.orders} color="text-amber-600" bg="bg-amber-50" onClick={() => openAnalytics("orders")} tid="analytics-orders" />
                </div>
                {!analytics.is_featured && (
                  <div className="mt-3 text-xs text-gray-500">{t("analytics.no_boost")}</div>
                )}
              </div>
            )}

            {item.owner_id === user.id && item.status !== "completed" && (
              <div className="mt-6 flex flex-wrap gap-2">
                <button data-testid="listing-edit-btn" onClick={editPost}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-gray-200 text-sm text-gray-800 hover:bg-gray-50">
                  <Pencil className="h-4 w-4" />Edit
                </button>
                <button data-testid="listing-complete-btn" onClick={markComplete}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-emerald-50 border border-emerald-200 text-sm text-emerald-800 hover:bg-emerald-100">
                  <CheckCircle2 className="h-4 w-4" />Mark completed
                </button>
                <button data-testid="listing-delete-btn" onClick={deletePost}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-rose-50 border border-rose-200 text-sm text-rose-700 hover:bg-rose-100">
                  <Trash2 className="h-4 w-4" />Delete
                </button>
              </div>
            )}

            {item.owner_id === user.id && (
              <div className="mt-6 rounded-2xl bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 p-5 flex items-center gap-4">
                <div className="h-12 w-12 rounded-2xl bg-amber-500 text-white flex items-center justify-center"><Zap className="h-6 w-6" /></div>
                <div className="flex-1">
                  <div className="font-semibold text-gray-900">{item.is_featured ? "This listing is featured" : t("boost.title")}</div>
                  <div className="text-xs text-gray-600 mt-0.5">{item.is_featured ? "Enjoy priority placement for 7 days." : t("boost.desc")}</div>
                </div>
                {!item.is_featured && (
                  <button data-testid="boost-btn" onClick={() => setShowBoost(true)}
                    className="px-5 py-2.5 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold">
                    {t("boost.price")}
                  </button>
                )}
              </div>
            )}

            {item.owner_id !== user.id && (
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button data-testid={LISTING.interestedBtn} onClick={interested} className={`px-4 py-3 rounded-full ${theme.bgSoft} ${theme.text} font-semibold flex items-center justify-center gap-2 border ${theme.border}`}>
                  <Heart className="h-4 w-4" />Interested
                </button>
                <button data-testid={LISTING.buyBtn} onClick={() => setShowCheckout(true)} className={`px-4 py-3 rounded-full ${theme.bg} text-white font-semibold flex items-center justify-center gap-2 shadow-lg`}>
                  <ShoppingBag className="h-4 w-4" />Buy Now
                </button>
                <button data-testid={LISTING.chatBtn} onClick={chat} className="px-4 py-3 rounded-full bg-white border border-gray-200 text-gray-800 font-semibold flex items-center justify-center gap-2">
                  <MessageCircle className="h-4 w-4" />Chat with Seller
                </button>
              </div>
            )}

            {item.owner_id !== user.id && item.lat && item.lng && (
              <a data-testid="get-directions" href={`https://www.google.com/maps/dir/?api=1&destination=${item.lat},${item.lng}&travelmode=driving`} target="_blank" rel="noreferrer"
                className="mt-3 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-blue-600 text-white text-sm font-semibold shadow-lg">
                <MapPin className="h-4 w-4" />Get driving directions
              </a>
            )}
          </motion.div>
        </div>
      </div>

      {showCheckout && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end md:items-center justify-center p-4">
          <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            className="w-full max-w-lg bg-white rounded-3xl p-8 premium-shadow">
            <div className="text-xs uppercase tracking-widest text-gray-500">Checkout</div>
            <h3 className="text-2xl font-bold mt-1">{item.name}</h3>

            <div className="mt-6 space-y-4">
              {item.kind === "product" && item.delivery_available && (
                <div>
                  <label className="text-xs font-semibold text-gray-600">Delivery choice</label>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <button type="button" data-testid="delivery-choice-delivery" onClick={() => setDeliveryChoice("delivery")}
                      className={`px-3 py-2.5 rounded-xl border text-sm font-medium ${deliveryChoice === "delivery" ? `${theme.bg} text-white border-transparent` : "bg-white border-gray-200 text-gray-800"}`}>🚚 Home delivery</button>
                    <button type="button" data-testid="delivery-choice-pickup" onClick={() => setDeliveryChoice("pickup")}
                      className={`px-3 py-2.5 rounded-xl border text-sm font-medium ${deliveryChoice === "pickup" ? `${theme.bg} text-white border-transparent` : "bg-white border-gray-200 text-gray-800"}`}>🏬 I'll pick up</button>
                  </div>
                </div>
              )}
              {item.kind === "product" && sellerListings.length > 0 && (
                <div data-testid="cart-extras" className="rounded-2xl bg-emerald-50/50 border border-emerald-100 p-3">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-semibold text-gray-700">🛒 Add more from {item.owner_name}</div>
                    {cartCount > 0 && <div className="text-[10px] uppercase tracking-widest text-emerald-700">{cartCount} added</div>}
                  </div>
                  <div className="mt-2 max-h-48 overflow-y-auto space-y-1.5">
                    {sellerListings.map((sl) => {
                      const cq = cart[sl.id] || 0;
                      return (
                        <div key={sl.id} className="flex items-center gap-2 bg-white rounded-xl border border-gray-100 p-2">
                          <div className="h-9 w-9 rounded-lg overflow-hidden bg-gray-100 shrink-0">
                            {sl.images?.[0] && <img src={sl.images[0]} alt="" className="w-full h-full object-cover" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-semibold text-gray-900 truncate">{sl.name}</div>
                            <div className="text-[10px] text-gray-500">₹{sl.price}{sl.unit ? ` /${sl.unit}` : ""}</div>
                          </div>
                          <div className="flex items-center gap-1">
                            <button type="button" data-testid={`cart-dec-${sl.id}`} onClick={() => setCart({ ...cart, [sl.id]: Math.max(0, cq - 1) })}
                              className="h-6 w-6 rounded-full bg-gray-100 text-sm">−</button>
                            <span className="w-6 text-center text-xs font-semibold">{cq}</span>
                            <button type="button" data-testid={`cart-inc-${sl.id}`} onClick={() => setCart({ ...cart, [sl.id]: cq + 1 })}
                              className={`h-6 w-6 rounded-full ${cq > 0 ? theme.bg + " text-white" : "bg-gray-100"} text-sm`}>+</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div>
                <label className="text-xs font-semibold text-gray-600">Quantity</label>
                <input type="number" min="1" value={qty} onChange={(e) => setQty(parseInt(e.target.value) || 1)}
                  className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3" />
              </div>
              {deliveryChoice === "delivery" && (
                <>
                  <div>
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-semibold text-gray-600">Delivery address</label>
                      <button type="button" data-testid="checkout-live-loc" onClick={useMyLocation} disabled={locating}
                        className="text-[11px] font-semibold text-emerald-700 hover:underline">
                        {locating ? "Detecting…" : buyerCoords.lat ? `📍 Location set (${buyerCoords.lat.toFixed(3)}, ${buyerCoords.lng.toFixed(3)})` : "📍 Use my live location"}
                      </button>
                    </div>
                    <textarea data-testid="checkout-address" value={addr} onChange={(e) => setAddr(e.target.value)} rows={2}
                      className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3" placeholder="House no, street, village, city, pincode" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-600">Contact Phone</label>
                      <input data-testid="checkout-phone" value={phone} onChange={(e) => setPhone(e.target.value)}
                        className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3" placeholder="10-digit mobile" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600">Note (optional)</label>
                      <input data-testid="checkout-note" value={note} onChange={(e) => setNote(e.target.value)}
                        className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3" placeholder="Landmark, timings, etc." />
                    </div>
                  </div>
                </>
              )}
              <div>
                <label className="text-xs font-semibold text-gray-600 flex items-center justify-between">
                  <span>✏️ Custom shopping list <span className="font-normal text-gray-400">(optional)</span></span>
                  <span className="text-[10px] text-gray-400">Type exactly what you need</span>
                </label>
                <textarea
                  data-testid="checkout-custom-items"
                  value={customItems}
                  onChange={(e) => setCustomItems(e.target.value)}
                  rows={2}
                  className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm"
                  placeholder="e.g. 1kg tomato, 2kg onion, 1 bottle oil"
                />
                {customItems.trim() && (
                  <p className="mt-1 text-[11px] text-emerald-700">Seller will see this list on the order.</p>
                )}
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600">Payment Method</label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {["UPI", "Card", "Net Banking", "COD"].map((m) => (
                    <button key={m} onClick={() => setPm(m)}
                      className={`px-4 py-3 rounded-xl border text-sm font-medium ${pm === m ? `${theme.bg} text-white border-transparent` : "bg-white border-gray-200 text-gray-800"}`}>
                      {m}
                    </button>
                  ))}
                </div>
                {pm === "COD" && item.kind === "product" && <p className="mt-2 text-[11px] text-amber-700">📦 A small ₹10 COD handling fee applies for cash on delivery.</p>}
              </div>
              <div data-testid="checkout-breakdown" className="rounded-xl bg-gray-50 p-4 text-sm">
                <div className="flex justify-between">
                  <span>{item.name} × {qty}</span>
                  <span>₹{item.price * qty}</span>
                </div>
                {Object.entries(cart).filter(([, q]) => q > 0).map(([lid, q]) => {
                  const sl = sellerListings.find((x) => x.id === lid);
                  if (!sl) return null;
                  return (
                    <div key={`cart-line-${lid}`} className="flex justify-between text-gray-700">
                      <span>{sl.name} × {q}</span>
                      <span>₹{sl.price * q}</span>
                    </div>
                  );
                })}
                {(() => {
                  const extrasSum = Object.entries(cart).reduce((sum, [lid, q]) => {
                    const sl = sellerListings.find((x) => x.id === lid); return sum + (sl ? sl.price * q : 0);
                  }, 0);
                  const primary = item.price * qty;
                  const items = primary + extrasSum;
                  const delivery = estimate?.delivery_fee || 0;
                  const cod = estimate?.cod_fee || 0;
                  const total = items + delivery + cod;
                  return (
                    <>
                      {delivery > 0 && (
                        <div className="flex justify-between">
                          <span>Delivery{estimate?.delivery_distance_km != null ? ` (~${estimate.delivery_distance_km} km)` : ""}</span>
                          <span>₹{delivery}</span>
                        </div>
                      )}
                      {cod > 0 && <div className="flex justify-between text-amber-700"><span>COD handling fee</span><span>₹{cod}</span></div>}
                      <div className="flex justify-between font-bold text-base mt-2 pt-2 border-t"><span>Total{cartCount > 0 ? ` (${cartTotalItems} items)` : ""}</span><span data-testid="checkout-total">₹{total}</span></div>
                    </>
                  );
                })()}
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button onClick={() => setShowCheckout(false)} className="flex-1 px-6 py-3 rounded-full border border-gray-200">Cancel</button>
              <button data-testid={LISTING.checkoutBtn} onClick={placeOrder} className={`flex-1 px-6 py-3 rounded-full ${theme.bg} text-white font-semibold`}>Confirm Order</button>
            </div>
          </motion.div>
        </div>
      )}

      {showPdf && item?.project_pdf && (
        <PdfViewer
          src={item.project_pdf}
          allowDownload={isOwner}
          filename={`${item.name || "project"}.pdf`}
          onClose={() => setShowPdf(false)}
        />
      )}

      {lightbox && imgs.length > 0 && (
        <FullscreenImage images={imgs} startIndex={imgIdx} onClose={() => setLightbox(false)} />
      )}


      {showBoost && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className="w-full max-w-md bg-white rounded-3xl p-8 premium-shadow text-center">
            <div className="mx-auto h-16 w-16 rounded-2xl bg-amber-500 text-white flex items-center justify-center"><Zap className="h-8 w-8" /></div>
            <h3 className="mt-6 text-2xl font-bold text-gray-900">{t("boost.title")}</h3>
            <p className="mt-2 text-sm text-gray-600">{t("boost.desc")}</p>
            <div className="mt-6 rounded-2xl bg-amber-50 border border-amber-200 p-4">
              <div className="text-3xl font-bold text-amber-700">₹99</div>
              <div className="text-xs text-amber-700/80 uppercase tracking-widest mt-1">for 7 days · Pin to top</div>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setShowBoost(false)} className="flex-1 py-3 rounded-full border border-gray-200">Cancel</button>
              <button data-testid="boost-pay" onClick={boost} className="flex-1 py-3 rounded-full bg-amber-500 text-white font-semibold">{t("boost.pay")}</button>
            </div>
          </motion.div>
        </div>
      )}

      {detailOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end md:items-center justify-center p-4" onClick={() => setDetailOpen(null)}>
          <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            className="w-full max-w-md bg-white rounded-3xl p-6 premium-shadow max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-gray-500">Details</div>
                <h3 className="text-xl font-bold capitalize">{detailOpen.kind}</h3>
              </div>
              <button onClick={() => setDetailOpen(null)} className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">✕</button>
            </div>
            <div className="mt-4 space-y-2">
              {loadingDetail && <div className="text-sm text-gray-500">Loading…</div>}
              {!loadingDetail && detailOpen.items.length === 0 && (
                <div className="text-sm text-gray-500 py-6 text-center">
                  {detailOpen.kind === "views" && "No one has viewed this post yet."}
                  {detailOpen.kind === "clicks" && "No clicks tracked yet."}
                  {detailOpen.kind === "interests" && "Nobody has shown interest yet."}
                  {detailOpen.kind === "orders" && "No orders placed yet."}
                </div>
              )}
              {detailOpen.items.map((it, i) => (
                <div key={`${detailOpen.kind}-${i}-${it.user_id || it.order_id}`}
                  className="rounded-2xl bg-white border border-gray-100 p-3 flex items-start gap-3">
                  <div className={`h-9 w-9 rounded-full ${theme.bg} text-white text-sm flex items-center justify-center font-bold`}>{(it.user_name || "?")[0]}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900 truncate">{it.user_name || "Anonymous"}</div>
                    {it.user_role && <div className="text-[10px] uppercase tracking-widest text-gray-400">{it.user_role.replace("_", " ")}</div>}
                    {it.message && <div className="text-xs text-gray-600 mt-1">"{it.message}"</div>}
                    {typeof it.total_amount !== "undefined" && (
                      <div className="text-xs text-gray-600 mt-1">Qty {it.quantity} · ₹{it.total_amount} · <b className="text-emerald-700">{it.status}</b></div>
                    )}
                    {it.count > 1 && <div className="text-[10px] text-gray-400 mt-0.5">×{it.count}</div>}
                  </div>
                  <div className="text-[10px] text-gray-400 shrink-0">{it.at ? new Date(it.at).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : ""}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      )}
    </AppShell>
  );
}

function AnalyticsCell({ icon: Icon, label, value, color, bg, onClick, tid }) {
  return (
    <button type="button" onClick={onClick} data-testid={tid}
      className={`text-center rounded-2xl p-3 -m-3 transition ${onClick ? "hover:bg-gray-50 cursor-pointer" : "cursor-default"}`}>
      <div className={`mx-auto h-10 w-10 rounded-xl ${bg} ${color} flex items-center justify-center`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="mt-2 text-xl font-bold text-gray-900">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
    </button>
  );
}

function relativeTime(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} day${Math.floor(diff / 86400) > 1 ? "s" : ""} ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function daysUntil(iso) {
  if (!iso) return "";
  const diff = (new Date(iso).getTime() - Date.now()) / (86400 * 1000);
  if (diff < 1) return `${Math.max(0, Math.ceil(diff * 24))} hr`;
  return `${Math.ceil(diff)} day${Math.ceil(diff) > 1 ? "s" : ""}`;
}
